import { MKEventBase, MKLog, MKProgramManager, MKSecureUrlManager, MKThumbnailManager, MKUtils } from "../common";
import { MKAssetType, MKDrmType, MKErrorCode, MKLogLevel, MKPlayerEvent, MKPlayerType, MKStreamType, MKTimeMode, MKViewMode, MKStartOffsetTimelineReference, MKPlaybackState} from "../../enums";
import {
    MKAdaptationAPI, MKAdBreakFinishedEvent, MKAdBreakStartedEvent, MKAdClickThroughEvent, MKAdCompanionEvent, MKAdMarkerEvent, MKAdQuartileEvent, MKAdStartedEvent,
    MKAdVerificationEvent, MKAirplayChangedEvent, MKAnalyticsConfig, MKAspectRatioChangedEvent, MKAudioAdaptationEvent, MKAudioChangedEvent, MKAudioDownloadQualityChangedEvent,
    MKAudioDownloadQualityChangeEvent, MKAudioPlaybackQualityChangedEvent, MKAudioQuality, MKAudioQualityChangedEvent, MKAudioQualityEvent, MKAudioTrack, MKAudioTrackEvent,
    MKCastAvailableEvent, MKCastStartedEvent, MKCastWaitingForDeviceEvent, MKDownloadedAudioData, MKDownloadedVideoData, MKDownloadFinishedEvent, MKDrmAPI, MKDrmLicenseAddedEvent,
    MKDurationChangedEvent, MKError, MKErrorEvent, MKFMTSASiDEvent, MKLatencyModeChangedEvent, MKLowLatencyAPI, MKMetadataChangedEvent, MKMetadataEvent, MKMetadataParsedEvent,
    MKPeriodSwitchedEvent, MKPlaybackEvent, MKPlaybackSpeedChangedEvent, MKPlayerAPI, MKBufferAPI, MKPlayerConfig, MKPlayerCoreAPI, MKPlayerEventBase, MKManifestAPI,
    MKPlayerResizedEvent, MKSubtitlesAPI, MKProgramRestrictionsEvent, MKProgramTimeChangedEvent, MKRollInfo, MKSeekEvent, MKSegmentMap, MKSegmentPlaybackEvent,
    MKSegmentRequestFinishedEvent, MKSessionConcurrencyAPI, MKSourceConfig, MKSourceOptions, MKSubtitleCueEvent, MKSubtitleEvent, MKTargetLatencyChangedEvent, MKThumbnail, MKTimeRange,
    MKUserInteractionEvent, MKVideoAdaptationEvent, MKVideoDownloadQualityChangeEvent, MKVideoPlaybackQualityChangedEvent, MKVideoQuality,MKVideoQualityChangedEvent,MKVideoQualityEvent,MKViewModeChangedEvent, MKViewModeOptions, MKVolumeChangedEvent, MKVSPPSessionIdEvent, MKSourceInfo,
    MKTimelineMarker,MKAdBreakConfig, MKModuleInfo, MKAdBreak, MKAd,
    MKLowLatencyConfig, 
} from "../../interfaces";
import { MKAdobeSessionConcurrencyRequest, MKAzukiRequest } from "../request";
import { MKHeaders, MKUserEngagement } from "../../alias";
import { version as SDK_VERSION } from "../../constants/MKVersion";
import { MKBitmovinPlayer } from "./MKBitmovinPlayer";
import { MKAdEvent, MKAdLinearityChangedEvent, MKAdManifestLoadedEvent, MKAdInteractionEvent, MKAdClickedEvent } from "../../interfaces/events";
import { MKGooglePAL } from "../request/MKGooglePal";
import { MKConstants } from "../../constants";
import { MKPlayerEngineType } from "../../enums/MKPlayerEngineType";

/**
 * MKPlayer.
 * @hidden
 */
export class MKPlayer extends MKEventBase implements MKPlayerAPI {
    readonly adaptation: MKAdaptationAPI;
    readonly buffer: MKBufferAPI;
    readonly drm: MKDrmAPI;
    readonly lowlatency: MKLowLatencyAPI;
    readonly manifest: MKManifestAPI;
    readonly subtitles: MKSubtitlesAPI;

    private rollInfo?: MKRollInfo;
    private player: MKPlayerCoreAPI;
    private videoContainer: HTMLElement;
    private playerConfig: MKPlayerConfig;
    private sourceConfig?: MKSourceConfig;
    private azukiRequest?: MKAzukiRequest;
    private googlepal?: MKGooglePAL;
    private nonce?: string;
    private sourceLoaded: boolean = false;
    private headers: MKHeaders | null = null;
    private programManager?: MKProgramManager;
    private analyticsConfig?: MKAnalyticsConfig;
    private thumbnailManager: MKThumbnailManager;
    private secureUrlManager?: MKSecureUrlManager;
    private isCdnFailoverInProgress: boolean = false;
    private sessionManager?: MKSessionConcurrencyAPI;
    private adobeSessionConcurrencyRequest?: MKAdobeSessionConcurrencyRequest;
    private currentSourceInfo = {} as MKSourceInfo;
    private failoverCdnList?: string[] | null = null;
    private failoverCdnProviderList?: string[] | null = null;
    private isAnalyticsEnabled?: boolean = false;
    private customDataValues: Record<string, string | undefined> | null = null;
    private lowLatencyPlayback: boolean = false;
    private lowLatencyAnalyticsInterval: any = null;
    private clientPrefersLowLatencyPlayback: boolean = false;
    private llCustomDataFieldDef: number = 0;
    /**
     * Creates a new MKPlayer instance.
     * 
     * @param videoContainer DOM element container for the player which contains the video element.
     * @param playerConfig player configuration.
     */
    constructor(videoContainer: HTMLElement, playerConfig: MKPlayerConfig) {
        super();
        this.playerConfig = playerConfig;
        this.videoContainer = videoContainer;
        this.thumbnailManager = new MKThumbnailManager();

        // Register player events if any specified via MKPlayerEventConfig
        if (playerConfig.events) {
            Object.entries(playerConfig.events).forEach(([key, value]) => {
                this.on(key as unknown as MKPlayerEvent, value);
            });
        }

        // Set log level if specified or set default log level
        MKLog.setLogLevel(playerConfig.log?.level ?? MKLogLevel.Debug);

        // Initialize the player engine and register listeners for player events
        this.player = new MKBitmovinPlayer(this.videoContainer, this.playerConfig);

        this.registerPlayerEventListeners();

        // initialize other player properties
        this.adaptation = this.player.adaptation;
        this.buffer = this.player.buffer;
        this.drm = this.player.drm;
        this.lowlatency = this.player.lowlatency;
        this.manifest = this.player.manifest;
        this.subtitles = this.player.subtitles;

        // Dump SDK version in the log.
        console.log(`### MKPlayer v${SDK_VERSION}`);
    }

    private setCustomData(): void {
        if (!this.analyticsConfig) return;

        this.isAnalyticsEnabled = true;
        const data: Record<string, string | undefined> = {};
      
        for (let i = 1; i <= 50; i++) {
          const key = `customData${i}` as keyof MKAnalyticsConfig;
          data[key] = this.analyticsConfig[key] as string | undefined;
        }
      
        data['experimentName'] = this.analyticsConfig.experimentName;
      
        this.customDataValues = data;
    }

    addCastMetadata(metadata: any): boolean {
        const castMetadata = {
            analytics: {
                type: MKConstants.AnalyticsType.Bitmovin, // bitmovin analytics
                enabled: this.isAnalyticsEnabled,
                data: {
                    config: {
                        key: this.analyticsConfig?.key,
                        videoId: this.analyticsConfig?.videoId
                    },
                    customData: this.customDataValues
                }
            }
        };

        if (metadata && typeof metadata === "string" && !!metadata && metadata === "session_manager_azuki" && this.azukiRequest) {
            const beaconData = this.azukiRequest.getBeaconData();
            const fullUrl = MKUtils.updateURLQueryParams(beaconData.requestUrl,beaconData.requestOptions.queryParams);
            Object.assign(castMetadata, {
                type : 0,
                interval : 30000,
                url : fullUrl,
                headers:{
                    AuthorizationToken : beaconData.requestOptions.headers.AuthorizationToken,
                    DeviceProfile : beaconData.requestOptions.headers.DeviceProfile,
                },
                beaconRequestBody : {
                    beacon : {
                        complete : beaconData.requestOptions.body.beacon.complete,
                        inhome : beaconData.requestOptions.body.beacon.inhome,
                        live : beaconData.requestOptions.body.beacon.isLive,
                    }
                }
            });
            return this.player.addCastMetadata(castMetadata);
        } else if (metadata && typeof metadata === "string" && !!metadata && metadata === "session_manager_adobe" && this.sourceConfig?.registered?.adobePrimetimeConfig) {
            const adobePrimeTimeConfig = this.sourceConfig.registered.adobePrimetimeConfig;
            Object.assign(castMetadata, {
                type : 1,
                isProduction : adobePrimeTimeConfig.isStaging ?? false,
                applicationId : adobePrimeTimeConfig.applicationId,
                requesterId : adobePrimeTimeConfig.requesterId,
                resourceId : adobePrimeTimeConfig.resourceId,
                userId : adobePrimeTimeConfig.userId,
                upStreamUserId : adobePrimeTimeConfig.upstreamUserId,
                slmt : adobePrimeTimeConfig.slmt,
                mvpd : adobePrimeTimeConfig.mvpd,
            });
            return this.player.addCastMetadata(castMetadata);
        }
        return this.player.addCastMetadata(metadata);
    }

    sendCastKssKey(keys: any): void {
        if ('X-MK-Signature' in keys && 'AuthorizationToken' in keys) {
            this.player.addCastMetadata({ kssKey: keys });
        }
    }

    addTimelineMarker(timelineMarker: MKTimelineMarker): void {
        this.player.addTimelineMarker(timelineMarker);
    }

    castStop(): void {
        this.player.castStop();
    }

    async scheduleAd(AdBreakConfig: MKAdBreakConfig): Promise<MKAdBreak[] | undefined> {
        try {
            return this.player.scheduleAd(AdBreakConfig)
        } catch (err) {
            return [];
        }
    }

    async skipAd(): Promise<void> {
        try {
            return this.player.skipAd();
        } catch (err) {
            return;
        }
    }
    
    discardAdBreak(adBreakId: string): void {
        this.player.discardAdBreak(adBreakId);
    }

    getActiveAd(): MKAd {
        return this.player.getActiveAd();
    }

    getActiveAdBreak(): MKAdBreak {
        return this.player.getActiveAdBreak();
    }

    getModuleInfo(): MKModuleInfo {
        return this.player.getModuleInfo();
    }

    isLinearAdActive(): boolean {
        return this.player.isLinearAdActive();
    }

    listAd(): MKAdBreak[] {
        return this.player.listAd();
    }

    castVideo(): void {
        this.player.castVideo();
    }

    async destroy(): Promise<void> {
        if (this.sourceLoaded) {
            // do everything unload does except calling unload
            // stop session concurrency notifications
            this.sessionManager?.stopSession()
            .catch((error) => {
                MKLog.warn("stopSession failed for sesison concurrency with error: ", JSON.stringify(error));
            });

            // disable thumbnails
            if (this.playerConfig.enableHlsImageMediaPlaylistSupport) {
                this.thumbnailManager.disable();
            }

            // reset the video element on unload
            const videoElement = this.player.getVideoElement();
            if (videoElement) {
                videoElement.src = "";
                await videoElement.setMediaKeys(null);
            }

            // reset the sourceLoaded flag
            this.sourceLoaded = false;
        }

        // destroy the player
        return this.player.destroy();
    }

    getAspectRatio(): number {
        return this.player.getAspectRatio();
    }

    getAudio(): MKAudioTrack | null {
        return this.player.getAudio();
    }

    getAudioQuality(): MKAudioQuality {
        return this.player.getAudioQuality();
    }

    getAvailableAudio(): MKAudioTrack[] {
        return this.player.getAvailableAudio();
    }

    getAvailableAudioQualities(): MKAudioQuality[] {
        return this.player.getAvailableAudioQualities();
    }

    getAvailableSegments(): MKSegmentMap {
        return this.player.getAvailableSegments();
    }

    getAvailableVideoQualities(): MKVideoQuality[] {
        return this.player.getAvailableVideoQualities();
    }

    getBufferedRanges(): MKTimeRange[] {
        return this.player.getBufferedRanges();
    }

    getConfig(mergedConfig?: boolean): MKPlayerConfig {
        return this.player.getConfig(mergedConfig);
    }

    getCurrentTime(mode?: MKTimeMode): number {
        return this.player.getCurrentTime(mode);
    }

    getDownloadedAudioData(): MKDownloadedAudioData {
        return this.player.getDownloadedAudioData();
    }

    getDownloadedVideoData(): MKDownloadedVideoData {
        return this.player.getDownloadedVideoData();
    }

    getDroppedVideoFrames(): number {
        return this.player.getDroppedVideoFrames();
    }

    getDuration(): number {
        return this.player.getDuration();
    }

    getLiveLatency(): number {
        return this.player.getLiveLatency();
    }

    getMaxTimeShift(): number {
        return this.player.getMaxTimeShift();
    }

    getPlaybackAudioData(): MKAudioQuality {
        return this.player.getPlaybackAudioData();
    }

    getPlaybackSpeed(): number {
        return this.player.getPlaybackSpeed();
    }

    getPlaybackVideoData(): MKVideoQuality {
        return this.player.getPlaybackVideoData();
    }

    getPlayerType(): MKPlayerType {
        return this.player.getPlayerType();
    }

    getPlayerEngine(type: MKPlayerEngineType): any {
        return this.player.getPlayerEngine(type);
    }

    getSeekableRange(mode?: MKTimeMode): MKTimeRange {
        return this.player.getSeekableRange(mode);
    }

    getSource(): MKSourceConfig | null {
        return this.player.getSource();
    }

    getSourceConfig(): Object | null {
        const mkSourceConfig: any = this.sourceConfig;
        if (mkSourceConfig) {
            if (this.sourceConfig?.registered?.environmentConfig.kssUrl && this.playerConfig.enableSecureUrl) {
                mkSourceConfig['X-MK-Signature'] = mkSourceConfig['X-MK-Signature'] || {};              
                mkSourceConfig['X-MK-Signature'].manifest = this.headers ? this.headers['X-MK-Signature'] : null;
            }
            if (this.sourceConfig?.registered?.environmentConfig.kssUrl && this.playerConfig.enableWidevineEncryption) {
                mkSourceConfig['X-MK-Signature'] = mkSourceConfig['X-MK-Signature'] || {};
                mkSourceConfig['X-MK-Signature'].license = mkSourceConfig.drm?.widevine?.headers["X-MK-Signature"];         // license signature
                delete mkSourceConfig.drm?.widevine?.headers?.["X-MK-Signature"];               // remove X-MK-Signature from drm headers.
            }
            return mkSourceConfig;
        }
        return null;
    }

    getStreamType(): MKStreamType {
        return this.player.getStreamType();
    }

    getThumbnail(time: number): MKThumbnail | null {
        // If sideloaded thumbnail tracks are specified that has preference over
        // the thumbnails found in the stream manifest.
            let thumbnail = this.player.getThumbnail(time);

        // If HLS image media playlist support is enabled
        if (thumbnail == null && this.playerConfig.enableHlsImageMediaPlaylistSupport && this.player.getStreamType() == MKStreamType.Hls) {
            thumbnail = this.thumbnailManager.getThumbnail(time);
        }
        
        return thumbnail;
    }

    getTimeShift(): number {
        return this.player.getTimeShift();
    }

    getTimelineMarkers(): MKTimelineMarker[] {
        return this.player.getTimelineMarkers();
    }

    getTotalStalledTime(): number {
        return this.player.getTotalStalledTime();
    }

    getVersion(): string {
        return SDK_VERSION;
    }

    getVideoContainer(): HTMLElement {
        return this.player.getVideoContainer();
    }

    getVideoElement(): HTMLVideoElement | undefined {
        return this.player.getVideoElement();
    }

    getVideoQuality(): MKVideoQuality {
        return this.player.getVideoQuality();
    }

    getViewMode(): MKViewMode {
        return this.player.getViewMode();
    }

    getVolume(): number {
        return this.player.getVolume();
    }

    hasEnded(): boolean {
        return this.player.hasEnded();
    }

    isAirplayActive(): boolean {
        return this.player.isAirplayActive();
    }

    isAirplayAvailable(): boolean {
        return this.player.isAirplayAvailable();
    }

    isCastAvailable(): boolean {
        return this.player.isCastAvailable();
    }

    isCasting(): boolean {
        return this.player.isCasting();
    }

    isLive(): boolean {
        return this.player.isLive();
    }

    isLowLatencyPlayback(): boolean {
        return this.lowLatencyPlayback;
    }

    isMuted(): boolean {
        return this.player.isMuted();
    }

    isPaused(): boolean {
        return this.player.isPaused();
    }

    isPlaying(): boolean {
        return this.player.isPlaying();
    }

    isStalled(): boolean {
        return this.player.isStalled();
    }

    isViewModeAvailable(viewMode: MKViewMode): boolean {
        return this.player.isViewModeAvailable(viewMode);
    }

    async load(sourceConfig: MKSourceConfig, disableSeeking?: boolean): Promise<void> {
        // unload previous source before loading new source
        if (this.sourceLoaded) {
            await this.unload();
        }

        let supportedDrm: string[] | undefined;
        if (sourceConfig.externalSourceLicenseUrl) {
            supportedDrm = await this.drm.getSupportedDRM();
        }

        /**
         * Source selection preference order
         * 1. HLS or DASH or SMOOTH or Progressive source (direct source)
         * 3. Registered source (mediakind source with mediaId and environment config)
         */

        return new Promise<void>((resolve, reject) => {
            // initialize or reset
            this.rollInfo = undefined;
            this.sourceLoaded = false;
            this.azukiRequest = undefined;
            this.sourceConfig = undefined;
            this.programManager = undefined;
            this.sessionManager = undefined;
            this.secureUrlManager = undefined;
            this.isCdnFailoverInProgress = false;
            this.adobeSessionConcurrencyRequest = undefined;
            sourceConfig = Object.assign({}, sourceConfig);
            this.currentSourceInfo = {
                timeTakenForSourceLoadSetup: 0,
                secureUrlSignatureTime: 0,
                streamUrl: "",
                streamType: MKStreamType.Unknown,
                drmType: MKDrmType.Unknown,
                azukiRequestTime: {
                    registration: 0,
                    roll: 0,
                    pendingBeacon: 0
                }
            };
            const loadStartTime = MKUtils.getCurrentTimestamp();

            // Indicate that the source load has begun
            this.emit(MKPlayerEvent.SourceLoad, {
                type: MKPlayerEvent.SourceLoad,
                timestamp: MKUtils.getCurrentTimestamp(),
                customData: sourceConfig // user specified sourceConfig
            });

            if (sourceConfig.enableLowLatency) {
                this.clientPrefersLowLatencyPlayback = true;
            } else {
                this.clientPrefersLowLatencyPlayback = false;
            }

            if (sourceConfig.live && sourceConfig.live.lowLatency) {
                sourceConfig.live.lowLatency = this.setLowLatencyConfig(sourceConfig.live.lowLatency);
                this.lowLatencyPlayback = true;
            }

            if (sourceConfig.hls !== undefined || sourceConfig.dash !== undefined || sourceConfig.smooth !== undefined || sourceConfig.progressive !== undefined) {
                if (sourceConfig.externalSourceLicenseUrl) {
                    if (supportedDrm?.[0].indexOf("widevine")) {
                        sourceConfig.drm = {
                            widevine: {
                                LA_URL: sourceConfig.externalSourceLicenseUrl,
                            }
                        }
                    } else if (supportedDrm?.[0].indexOf("playready")) {
                        sourceConfig.drm = {
                            playready: {
                                LA_URL: sourceConfig.externalSourceLicenseUrl,
                            }
                        }
                    } else if (supportedDrm?.[0].indexOf("fairplay") && sourceConfig.externalSourceCertificateUrl) {
                        sourceConfig.drm = {
                            fairplay: {
                                LA_URL: sourceConfig.externalSourceLicenseUrl,
                                certificateURL: sourceConfig.externalSourceCertificateUrl,
                            }
                        }
                        delete sourceConfig.externalSourceCertificateUrl;
                    }
                    delete sourceConfig.externalSourceLicenseUrl;
                }

                // Direct source config
                // update source config and start playback
                this.sourceConfig = sourceConfig;
                this.player.load(sourceConfig, disableSeeking)
                .then(() => {return resolve();})
                .catch((error) => {return reject(error);});
            } else if (sourceConfig.registered !== undefined) {
                // For registered source
                if (sourceConfig.registered.environmentConfig.kssUrl && this.playerConfig.enableSecureUrl) {
                    // new secureUrl manager for this session
                    this.secureUrlManager = new MKSecureUrlManager(sourceConfig.registered.environmentConfig.authToken,
                    sourceConfig.registered.environmentConfig.kssUrl);
                }

                // new azuki requests for this session
                this.azukiRequest = new MKAzukiRequest(sourceConfig ,this.playerConfig ,this.onSessionConcurrencyNotificationError.bind(this));
                this.azukiRequest.rollRequest()
                .then(async (rollInfo: MKRollInfo) => {
                    // Update rollInfo
                    this.rollInfo = rollInfo;
                    if (this.rollInfo.player_config) {
                        MKAzukiRequest.enableUserEngagementData = (JSON.parse(MKUtils.base64Decode(this.rollInfo.player_config))).enableUserEngagementData ? true : false;
                    }
                    if (sourceConfig.assetType === MKAssetType.VOD && MKAzukiRequest.enableUserEngagementData) {
                        if (sourceConfig?.sourceOptions?.startOffsetTimelineReference === MKStartOffsetTimelineReference.End && sourceConfig.sourceOptions?.startOffset) {
                            MKAzukiRequest.startPlaybackPosition = this.player.getDuration() - sourceConfig.sourceOptions?.startOffset;
                        } else {
                            MKAzukiRequest.startPlaybackPosition = sourceConfig.sourceOptions?.startOffset || 0;
                        }
                    }                  
                    
                    // updating the enable low latency flag as per the roll response
                    if (this.rollInfo.isLowLatencyPlayback) {
                        sourceConfig.enableLowLatency = true;
                        this.lowLatencyPlayback = true;
                    } else {
                        sourceConfig.enableLowLatency = false;
                        if (sourceConfig.live) {
                            sourceConfig.live.lowLatency = undefined;
                        }
                        this.lowLatencyPlayback = false;
                    }

                    // Initialize program manager
                    this.programManager = new MKProgramManager(this.azukiRequest);

                    // validate program restrictions
                    const controlBlockingRestrictions = this.programManager.validateProgramRestrictions(this.rollInfo.programInfo);
                    if (controlBlockingRestrictions.length > 0) {
                        this.emitProgramRestrictionsEvent(controlBlockingRestrictions);
                    }

                    // notify FMTS ASiD Init Token if available
                    if (this.rollInfo.asidInitToken && this.rollInfo.asidInitToken.length > 0) {
                        // emit fmts asid event
                        const fmtsASiDEventData: MKFMTSASiDEvent = {
                            initToken: this.rollInfo.asidInitToken,
                            assetId: this.rollInfo.mediaId,
                            timestamp: MKUtils.getCurrentTimestamp(),
                            type: MKPlayerEvent.FMTSASiD,
                        };
                        this.emit(MKPlayerEvent.FMTSASiD, fmtsASiDEventData);
                    }

                    // update program info list for live source which will ensure that
                    // we have previous, current and next program information with us.
                    if (this.rollInfo.programInfo.timeRange && this.rollInfo.isLive) {
                        // emit program time changed event
                        this.emitProgramTimeChangedEvent(this.rollInfo.programInfo.timeRange);

                        // update program info list
                        this.programManager?.updateProgramInfoList(this.rollInfo.programInfo);
                    }

                    // update the start time in manifest for live asset with programLoadTime to support restart of live programs.
                    if (sourceConfig.sourceOptions?.programLoadTime !== undefined && sourceConfig.assetType === MKAssetType.Live) {
                        this.rollInfo.sourceUrl = MKUtils.updateURLQueryParams( this.rollInfo.sourceUrl as string, { "start": sourceConfig.sourceOptions.programLoadTime });
                    }

                    // append cdnTokens to this.rollInfo.sourceUrl if any cdnTokens are available
                    if (sourceConfig.cdnOptions?.cdnTokens) {
                        this.rollInfo.sourceUrl = MKUtils.updateURLQueryParams(this.rollInfo.sourceUrl, sourceConfig.cdnOptions.cdnTokens);
                        if(this.rollInfo.failoverSourceUrl?.length) {
                            this.rollInfo.failoverSourceUrl = this.rollInfo.failoverSourceUrl.map(url => MKUtils.updateURLQueryParams(url, sourceConfig.cdnOptions?.cdnTokens));
                        }
                    }
                    const muteValue = this.player.isMuted() ? 1 : 0;
                    this.rollInfo.sourceUrl = MKUtils.updateURLQueryParams(this.rollInfo.sourceUrl,{"vpmute": muteValue});
                    if(this.rollInfo.failoverSourceUrl?.length) {
                        this.rollInfo.failoverSourceUrl = this.rollInfo.failoverSourceUrl.map(url => MKUtils.updateURLQueryParams(url,{"vpmute": muteValue}));
                    }

                    // get signature for manifest url if secureUrlManager is available
                    if (this.secureUrlManager) {
                        try {
                            // get the signature for the sourceUrl (which is the manifestUrl)
                            const signature = await this.secureUrlManager.getSignature(this.rollInfo.sourceUrl);
                            // update player headers with headers for secureUrl
                            this.updateHttpHeaders({
                                "X-MK-Signature": signature,
                                "AuthorizationToken": sourceConfig.registered?.environmentConfig.authToken!
                            });

                            // set headers
                            this.player.setHttpHeaders(this.headers);
                            this.thumbnailManager.setHttpHeaders(this.headers);
                        } catch {
                            MKLog.warn("Failed to get signature from secureUrlManager!");
                        }
                    }

                    // generate a random number and stop analytics call if it is greater than analytics_publish_percent
                    if(this.rollInfo.analytics_publish_percent){
                        const randomDecimal = Math.random();
                        const randomNumber = Math.floor(randomDecimal * 99) + 1;
                        if (randomNumber >= this.rollInfo.analytics_publish_percent && (sourceConfig.analytics || this.playerConfig.analytics)) {
                            this.playerConfig.analytics = false;
                            sourceConfig.analytics = undefined;
                            this.analyticsConfig = undefined;
                            MKLog.warn(`Disabled Bitmovin Analytics session: Generated random number - ${randomNumber} is greater than threshold value - ${this.rollInfo.analytics_publish_percent}`);
                        } else if (randomNumber < this.rollInfo.analytics_publish_percent && (sourceConfig.analytics || this.playerConfig.analytics)) {
                            this.analyticsConfig = {
                                ...(this.playerConfig.analytics ?? {}),
                                ...(sourceConfig.analytics ?? {})
                            };

                            this.setCustomData();

                            // assign cdnProvider value if it is not - undefined, null, empty, or just whitespace
                            if (this.analyticsConfig && !this.analyticsConfig.cdnProvider?.trim()) {
                                this.analyticsConfig.cdnProvider = this.rollInfo.sourceCdnProvider;
                            }

                            // assigning updated source config analytics
                            sourceConfig.analytics = this.analyticsConfig;
                            
                            MKLog.log(`Enabled Bitmovin Analytics session: Generated random number - ${randomNumber} is lesser than threshold value - ${this.rollInfo.analytics_publish_percent}`);
                        }
                    }
                   
                    if(this.playerConfig.enableGooglePal){
                        this.googlepal = new MKGooglePAL();
                        this.nonce = await this.googlepal.generateNonce();
                        this.rollInfo.sourceUrl = MKUtils.updateURLQueryParams(this.rollInfo.sourceUrl,{'givn': this.nonce});
                        if(this.rollInfo.failoverSourceUrl?.length)
                            this.rollInfo.failoverSourceUrl = this.rollInfo.failoverSourceUrl.map(url => MKUtils.updateURLQueryParams(url,{'givn': this.nonce as string}));
                    }

                   // If the asset type is catchup and if end time in the URL is in the future, then replace it with the current time to avoid playing catch up as live asset.
                    if(sourceConfig.assetType === MKAssetType.Catchup) {
                        const endParam = MKUtils.getURLQueryParam(this.rollInfo.sourceUrl, "end") as string;
                        const currentTime = MKUtils.getCurrentTimestamp();
                        if (MKUtils.dateISOStringToTimestamp(endParam) > currentTime) {
                            const endISODate = MKUtils.timestampToISOString(currentTime);
                            this.rollInfo.sourceUrl = MKUtils.updateURLQueryParams( this.rollInfo.sourceUrl as string, { "end": endISODate });
                            MKLog.debug("End time is in the future, Replacing End time to current time");
                        }
                    }

                    // Update source config from rollInfo
                    this.sourceConfig = this.updateSourceConfigFromRollInfo(sourceConfig, this.rollInfo);

                    // update analytics information (some others may be updated from SourceLoaded event handler)
                    this.currentSourceInfo.timeTakenForSourceLoadSetup = MKUtils.getCurrentTimestamp() - loadStartTime,
                    this.currentSourceInfo.azukiRequestTime = {
                        registration: MKAzukiRequest.responseTime.registration,
                        roll: MKAzukiRequest.responseTime.roll,
                        pendingBeacon: MKAzukiRequest.responseTime.pendingBeacons
                    },
                    this.currentSourceInfo.secureUrlSignatureTime = this.secureUrlManager?.getSignatureGenerationTime() ?? 0,
                    this.currentSourceInfo.drmType = this.rollInfo.drmType;

                    // Log CDN Failover as critical for last CDN and others as non critical in Bitmovin Analytics Dashboard
                    if(sourceConfig.analytics) {
                        Object.assign(sourceConfig.analytics, 
                            {
                                config: {
                                    errorTransformerCallback: {
                                        transform: (error: any, context: any) => {
                                            if (this.isCdnFailoverInProgress) {
                                                MKLog.debug("Severity updated for CDN failure: " + JSON.stringify(context));
                                                return { ...error, severity: "INFO" };
                                            }
                                        return error;
                                    }
                                }   
                            }
                        });
                    }

                    // Start playback
                    this.player.load(sourceConfig, disableSeeking)
                    .then(() => {return resolve();})
                    .catch((error) => { 
                        if (this.isCdnFailoverInProgress) return resolve();
                        return reject(error);
                    });
                })
                .catch((error: MKError) => {
                    // Handle roll request failure. We must emit the error event for this...
                    return reject(this.unloadAndEmitErrorEvent(error, false));
                });
            } else {
                return reject(new Error("Source type not supported!"));
            }
        });
    }

    mute(issuer?: string): void {
        this.player.mute(issuer);
    }

    pause(issuer?: string): void {
        this.player.pause(issuer);
    }

    play(issuer?: string): Promise<void> {
        return this.player.play(issuer);
    }

    preload(): void {
        this.player.preload();
    }

    reload(start?: string, sourceOptions?: MKSourceOptions): Promise<void> {
        return this.player.reload(start, sourceOptions);
    }

    removeTimelineMarker(timelineMarker: MKTimelineMarker): boolean {
        return this.player.removeTimelineMarker(timelineMarker);
    }

    seek(time: number, issuer?: string): boolean {
        return this.player.seek(time, issuer);
    }

    seekToLiveEdge(issuer?: string): void {
        this.player.seekToLiveEdge(issuer);
    }

    setAspectRatio(aspectratio: string | number): void {
        this.player.setAspectRatio(aspectratio);
    }

    setAudio(trackID: string): void {
        this.player.setAudio(trackID);
    }

    setAudioQuality(audioQualityID: string): void {
        this.player.setAudioQuality(audioQualityID);
    }

    setHttpHeaders(headers: MKHeaders | null): void {
        if (headers === null) {
            // clear set headers
            this.player.setHttpHeaders(headers);
            this.headers = null;
            MKAzukiRequest.customHeaders = null;
            return;
        }

        // set headers on the player...
        const authToken = MKUtils.pluckValue(headers, "AuthorizationToken");
        if (this.azukiRequest && this.secureUrlManager && typeof(authToken) === "string" && !!authToken) {
            // update authToken and generate a new signature for secureUrl and
            // set the given headers with new signature generated on the player
            this.azukiRequest.updateAuthToken(authToken);
            this.secureUrlManager.updateAuthToken(authToken)
            .then((signature) => {
                // add signature to the headers and set the headers
                headers["X-MK-Signature"] = signature;
                this.updateHttpHeaders(headers);
                this.player.setHttpHeaders(this.headers);
                this.thumbnailManager.setHttpHeaders(this.headers);
            })
            .catch((_) => {
                // set given headers (without new signature - lets roll with the old signature only!)
                MKLog.warn("Failed to get signature for the new authToken, setting the given headers on the player anyway!");
                this.updateHttpHeaders(headers);
                this.player.setHttpHeaders(this.headers);
                this.thumbnailManager.setHttpHeaders(this.headers);
            });
        } else {
            // set given headers
            this.updateHttpHeaders(headers);
            this.player.setHttpHeaders(this.headers);
            this.thumbnailManager.setHttpHeaders(this.headers);
        }
        MKAzukiRequest.customHeaders = this.headers;
    }

    setInhomeStatus(inhomeStatus: boolean): void {
        MKAzukiRequest.inhomeStatus = inhomeStatus;
    }

    setLocationDetail(locationDetail: string | null): void {
        if (locationDetail) {
            MKAzukiRequest.locationDetail = locationDetail;
        } else {
            delete MKAzukiRequest.locationDetail;
        }
    }

    setLogLevel(level: MKLogLevel): void {
        MKLog.setLogLevel(level);
        this.player.setLogLevel(level);
    }

    setPlaybackSpeed(speed: number): void {
        this.player.setPlaybackSpeed(speed);
    }

    setPosterImage(url: string, keepPersistent: boolean): void {
        this.player.setPosterImage(url, keepPersistent);
    }

    setUserEngagementData(userEngagementData?: MKUserEngagement | null ): void {
        if (userEngagementData) {
            MKAzukiRequest.userEngagementData = userEngagementData;
        }
    }

    setVideoElement(videoElement: HTMLVideoElement): void {
        this.player.setVideoElement(videoElement);
    }

    setVideoQuality(videoQualityID: string): void {
        this.player.setVideoQuality(videoQualityID);
    }

    setViewMode(viewMode: MKViewMode, options?: MKViewModeOptions): void {
        this.player.setViewMode(viewMode, options);
    }

    setVolume(volume: number, issuer?: string): void {
        this.player.setVolume(volume, issuer);
    }

    setAnalyticsCustomData(customData:MKAnalyticsConfig):void{
        this.player.setAnalyticsCustomData(customData);
    }

    setSubtitleStyle(styleOptions: any): void {
        this.player.setSubtitleStyle(styleOptions);
    }

    setCloudIdConfig(cloudIdInfo: string): void {
        if (cloudIdInfo) {
            MKAzukiRequest.cloudIdInfo = cloudIdInfo;
        }
    }

    showAirplayTargetPicker(): void {
        this.player.showAirplayTargetPicker();
    }

    async unload(): Promise<void> {
        // stop session concurrency notifications
        this.sessionManager?.stopSession()
        .catch((error) => {
            MKLog.warn("stopSession failed for sesison concurrency with error: ", JSON.stringify(error));
        });

        // disable thumbnails
        if (this.playerConfig.enableHlsImageMediaPlaylistSupport) {
            this.thumbnailManager.disable();
        }

        // reset the video element on unload
        const videoElement = this.player.getVideoElement();
        if (videoElement) {
            videoElement.src = "";
            await videoElement.setMediaKeys(null);
        }

        // Analytics tune data
        this.emit(MKPlayerEvent.SourceUnload, {
            type: MKPlayerEvent.SourceUnload,
            timestamp: MKUtils.getCurrentTimestamp()
        });

        return this.player.unload();
    }

    unmute(issuer?: string): void {
        this.player.unmute(issuer);
    }

    updateAuthToken(authToken: string): void {
        if (!this.azukiRequest) {
            MKLog.error("Available only for registered source!");
            return;
        }

        if (!!!authToken) {
            MKLog.error("Given authToken is not valid!");
            return;
        }

        // update azuki request with the new token
        this.azukiRequest.updateAuthToken(authToken);

        // update secure URL manager with the new token and get new signature for it
        this.secureUrlManager?.updateAuthToken(authToken)
        .then((signature: string) => {
            if (!!signature) {
                // update player headers with headers for secureUrl
                this.updateHttpHeaders({
                    "X-MK-Signature": signature,
                    "AuthorizationToken": authToken
                });

                // set headers
                this.player.setHttpHeaders(this.headers);
                this.thumbnailManager.setHttpHeaders(this.headers);
            }
        })
        .catch((_error) => {
            MKLog.warn("Something went wrong while setting http headers on the player for secureUrl signature and authToken!");
        });
    }
    
    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Class Private Methods
    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Update the player headers.
     *
     * Merges new headers with existing set of headers or clears all set headers when `null` is passed.
     *
     * **Note:** just updates the headers object, does not set it on the player. Must call `setHttpHeaders` to set the headers.
     *
     * @param headers headers to update or `null` to clear all headers.
     */
    private updateHttpHeaders(headers: MKHeaders | null): void {
        if (headers === null) {
            // clear the headers
            this.headers = null;
        } else {
            // update (merge with existing) headers
            this.headers = Object.assign({}, this.headers ?? {}, headers);

            // update the KSS Signed Keys to cast
            this.sendCastKssKey(this.headers);
        }
    }

    /**
     * Update the user specified source configuration object with the information in Roll response and return the same.
     * @param sourceConfig the user specified source configuration object to be updated
     * @param rollInfo the roll response information object.
     * @returns the updated source configuration object.
     */
    private updateSourceConfigFromRollInfo(sourceConfig: MKSourceConfig, rollInfo: MKRollInfo): MKSourceConfig {
        // DRM Config
        sourceConfig.drm = rollInfo.drm;

        // Update asset type for Live, Event and VOD
        if (sourceConfig.assetType !== MKAssetType.Catchup && sourceConfig.assetType !== MKAssetType.DVR) {
            sourceConfig.assetType = rollInfo.isLive ? (rollInfo.isLiveEvent ? MKAssetType.Event : MKAssetType.Live) : MKAssetType.VOD;
        }

        // Update sourceConfig for HLS or DASH sources
        if (rollInfo.streamType === MKStreamType.Hls) {
            sourceConfig.hls = rollInfo.sourceUrl;
        } else if (rollInfo.streamType === MKStreamType.Dash) {
            sourceConfig.dash = rollInfo.sourceUrl;
        }

        // message_id to get azuki public key
        if (rollInfo.message_id !== undefined) {
            sourceConfig.message_id = rollInfo.message_id;
        }

        // return updated sourceConfig
        return sourceConfig;
    }

    private async switchToFailoverSource(sourceConfig: MKSourceConfig, failoverCdnUrlList: string[], failoverCdnProvider: string[]): Promise<void> {
        if (!failoverCdnUrlList.length) {
            MKLog.warn("Failover CDN Url list is empty, cannot switch.");
            return;
        }

        if (!this.failoverCdnList?.length) {
            this.failoverCdnList = failoverCdnUrlList;
            this.failoverCdnProviderList = failoverCdnProvider;
        }

        let selectedFailoverUrl = this.failoverCdnList?.shift();
        let selectedFailoverCdnProvider = this.failoverCdnProviderList?.shift();

        if (!selectedFailoverUrl) {
            MKLog.warn("No valid failover CDN found.");
            return;
        }

        if (this.secureUrlManager) {
            try {
                // get the signature for the sourceUrl (which is the manifestUrl)
                const signature = await this.secureUrlManager.getSignature(selectedFailoverUrl);

                // update player headers with headers for secureUrl
                this.updateHttpHeaders({
                    "X-MK-Signature": signature,
                    "AuthorizationToken": sourceConfig.registered?.environmentConfig.authToken!
                });

                // set the headers
                this.player.setHttpHeaders(this.headers);
                this.thumbnailManager.setHttpHeaders(this.headers);
            } catch {
                MKLog.warn("Failed to get signature from secureUrlManager!");
            }
        }

        // replace source with failover source
        if(sourceConfig.dash){
            sourceConfig.dash = selectedFailoverUrl;
        } else if (sourceConfig.hls) {
            sourceConfig.hls = selectedFailoverUrl;
        }

        if (sourceConfig.analytics) {
            sourceConfig.analytics.cdnProvider = selectedFailoverCdnProvider;
        }

        // Update isCdnFailoverInProgress flag to indicate if CDNs are exhausted
        if (this.failoverCdnList.length === 0) {
            this.isCdnFailoverInProgress = true;
        } else {
            this.isCdnFailoverInProgress = false; 
        }

        // Update source config
        this.sourceConfig = sourceConfig;

        // Start playback
        MKLog.debug("Attempting to start playback with secondary/failover source!");
        return this.player.load(sourceConfig);
    }

    /**
     * Register for player events.
     */
    private registerPlayerEventListeners(): void {
        this.player.on(MKPlayerEvent.AdBreakFinished, (event: MKAdBreakFinishedEvent) => {
            this.emit(MKPlayerEvent.AdBreakFinished, event);
        });
        this.player.on(MKPlayerEvent.AdBreakStarted, (event: MKAdBreakStartedEvent) => {
            this.emit(MKPlayerEvent.AdBreakStarted, event);
        });
        this.player.on(MKPlayerEvent.AdFinished, (event: MKAdBreakFinishedEvent) => {
            this.emit(MKPlayerEvent.AdFinished, event);
        });
        this.player.on(MKPlayerEvent.AdClicked, (event: MKAdClickedEvent) => {
            this.emit(MKPlayerEvent.AdClicked, event);
        });
        this.player.on(MKPlayerEvent.AdSkipped, (event: MKAdEvent) => {
            this.emit(MKPlayerEvent.AdSkipped, event);
        });
        this.player.on(MKPlayerEvent.AdLinearityChanged, (event: MKAdLinearityChangedEvent) => {
            this.emit(MKPlayerEvent.AdLinearityChanged, event);
        });
        this.player.on(MKPlayerEvent.AdManifestLoaded, (event: MKAdManifestLoadedEvent) => {
            this.emit(MKPlayerEvent.AdManifestLoaded, event);
        });
        this.player.on(MKPlayerEvent.AdError, (event: MKErrorEvent) => {
            this.emit(MKPlayerEvent.AdError, event);
        });
        this.player.on(MKPlayerEvent.AdInteraction, (event: MKAdInteractionEvent) => {
            this.emit(MKPlayerEvent.AdInteraction, event);
        });
        this.player.on(MKPlayerEvent.AdMarker, (event: MKAdMarkerEvent) => {
            this.emit(MKPlayerEvent.AdMarker, event);
        });
        this.player.on(MKPlayerEvent.AdClickThrough, (event: MKAdClickThroughEvent) => {
            this.emit(MKPlayerEvent.AdClickThrough, event);
        });
        this.player.on(MKPlayerEvent.AdCompanion, (event: MKAdCompanionEvent) => {
            this.emit(MKPlayerEvent.AdCompanion, event);
        });
        this.player.on(MKPlayerEvent.AdVerification, (event: MKAdVerificationEvent) => {
            this.emit(MKPlayerEvent.AdVerification, event);
        });
        this.player.on(MKPlayerEvent.AdQuartile, (event: MKAdQuartileEvent) => {
            this.emit(MKPlayerEvent.AdQuartile, event);
        });
        this.player.on(MKPlayerEvent.AdStarted, (event: MKAdStartedEvent) => {
            this.emit(MKPlayerEvent.AdStarted, event);
        });
        this.player.on(MKPlayerEvent.AirplayAvailable, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.AirplayAvailable, event);
        });
        this.player.on(MKPlayerEvent.AirplayChanged, (event: MKAirplayChangedEvent) => {
            this.emit(MKPlayerEvent.AirplayChanged, event);
        });
        this.player.on(MKPlayerEvent.AspectRatioChanged, (event: MKAspectRatioChangedEvent) => {
            this.emit(MKPlayerEvent.AspectRatioChanged, event);
        });
        this.player.on(MKPlayerEvent.AudioAdaptation, (event: MKAudioAdaptationEvent) => {
            this.emit(MKPlayerEvent.AudioAdaptation, event);
        });
        this.player.on(MKPlayerEvent.AudioAdded, (event: MKAudioTrackEvent) => {
            this.emit(MKPlayerEvent.AudioAdded, event);
        });
        this.player.on(MKPlayerEvent.AudioChanged, (event: MKAudioChangedEvent) => {
            this.emit(MKPlayerEvent.AudioChanged, event);
        });
        this.player.on(MKPlayerEvent.AudioDownloadQualityChange, (event: MKAudioDownloadQualityChangeEvent) => {
            this.emit(MKPlayerEvent.AudioDownloadQualityChange, event);
        });
        this.player.on(MKPlayerEvent.AudioDownloadQualityChanged, (event: MKAudioDownloadQualityChangedEvent) => {
            this.emit(MKPlayerEvent.AudioDownloadQualityChanged, event);
        });
        this.player.on(MKPlayerEvent.AudioPlaybackQualityChanged, (event: MKAudioPlaybackQualityChangedEvent) => {
            this.emit(MKPlayerEvent.AudioPlaybackQualityChanged, event);
        });
        this.player.on(MKPlayerEvent.AudioQualityAdded, (event: MKAudioQualityEvent) => {
            this.emit(MKPlayerEvent.AudioQualityAdded, event);
        });
        this.player.on(MKPlayerEvent.AudioQualityChanged, (event: MKAudioQualityChangedEvent) => {
            this.emit(MKPlayerEvent.AudioQualityChanged, event);
        });
        this.player.on(MKPlayerEvent.AudioQualityRemoved, (event: MKAudioQualityEvent) => {
            this.emit(MKPlayerEvent.AudioQualityRemoved, event);
        });
        this.player.on(MKPlayerEvent.AudioRemoved, (event: MKAudioTrackEvent) => {
            this.emit(MKPlayerEvent.AudioRemoved, event);
        });
        this.player.on(MKPlayerEvent.CastAvailable, (event: MKCastAvailableEvent) => {
            this.emit(MKPlayerEvent.CastAvailable, event);
        });
        this.player.on(MKPlayerEvent.CastStart, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.CastStart, event);
        });
        this.player.on(MKPlayerEvent.CastStarted, (event: MKCastStartedEvent) => {
            if (this.sessionManager) {
                this.sessionManager?.PauseSession();
                this.addCastMetadata(this.sessionManager.sessionManager);
            }

            // update the KSS Signed Keys to cast
            this.sendCastKssKey(this.headers);

            this.emit(MKPlayerEvent.CastStarted, event);
        });
        this.player.on(MKPlayerEvent.CastStopped, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.CastStopped, event);
            this.sessionManager?.startSession();
        });
        this.player.on(MKPlayerEvent.CastWaitingForDevice, (event: MKCastWaitingForDeviceEvent) => {
            this.emit(MKPlayerEvent.CastWaitingForDevice, event);
        });
        this.player.on(MKPlayerEvent.CueEnter, (event: MKSubtitleCueEvent) => {
            this.emit(MKPlayerEvent.CueEnter, event);
        }),
        this.player.on(MKPlayerEvent.CueExit, (event: MKSubtitleCueEvent) => {
            this.emit(MKPlayerEvent.CueExit, event);
        }),
        this.player.on(MKPlayerEvent.CueParsed, (event: MKSubtitleCueEvent) => {
            this.emit(MKPlayerEvent.CueParsed, event);
        }),
        this.player.on(MKPlayerEvent.CueUpdate, (event: MKSubtitleCueEvent) => {
            this.emit(MKPlayerEvent.CueUpdate, event);
        }),
        this.player.on(MKPlayerEvent.DVRWindowExceeded, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.DVRWindowExceeded, event);
        });
        this.player.on(MKPlayerEvent.Destroy, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.Destroy, event);
        });
        this.player.on(MKPlayerEvent.DownloadFinished, (event: MKDownloadFinishedEvent) => {
            this.emit(MKPlayerEvent.DownloadFinished, event);
        });
        this.player.on(MKPlayerEvent.DrmLicenseAdded, (event: MKDrmLicenseAddedEvent) => {
            this.emit(MKPlayerEvent.DrmLicenseAdded, event);
        });
        this.player.on(MKPlayerEvent.DurationChanged, (event: MKDurationChangedEvent) => {
            this.emit(MKPlayerEvent.DurationChanged, event);
        });
        this.player.on(MKPlayerEvent.Error, (event: MKErrorEvent) => {
            this.clearLowLatencyInterval();
            const mkError = event as unknown as MKError;
            if((mkError.code == MKErrorCode.PLAYER_NETWORK_MANIFEST_DOWNLOAD_TIMEOUT || 
                mkError.code == MKErrorCode.PLAYER_SOURCE_MANIFEST_INVALID || 
                mkError.code == MKErrorCode.PLAYER_SOURCE_COULD_NOT_LOAD_MANIFEST || 
                mkError.code == MKErrorCode.PLAYER_SOURCE_HLS_STREAM_ERROR || 
                mkError.code == MKErrorCode.NETWORK_ERROR) && this.sourceConfig && !!this.rollInfo?.failoverSourceUrl && !!this.rollInfo?.failoverCdnProvider && !this.isCdnFailoverInProgress && !this.sourceLoaded) {
                this.isCdnFailoverInProgress = true;
                this.switchToFailoverSource(this.sourceConfig, this.rollInfo.failoverSourceUrl, this.rollInfo.failoverCdnProvider);
            }else {
                this.isCdnFailoverInProgress = false;
                this.emit(MKPlayerEvent.Error, event);
            };
        });
        this.player.on(MKPlayerEvent.LatencyModeChanged, (event: MKLatencyModeChangedEvent) => {
            this.emit(MKPlayerEvent.LatencyModeChanged, event);
        });
        this.player.on(MKPlayerEvent.Metadata, (event: MKMetadataEvent) => {
            this.emit(MKPlayerEvent.Metadata, event);
        });
        this.player.on(MKPlayerEvent.MetadataChanged, (event: MKMetadataChangedEvent) => {
            this.emit(MKPlayerEvent.MetadataChanged, event);
        });
        this.player.on(MKPlayerEvent.MetadataParsed, (event: MKMetadataParsedEvent) => {
            this.emit(MKPlayerEvent.MetadataParsed, event);
        });
        this.player.on(MKPlayerEvent.Muted, (event: MKUserInteractionEvent) => {
            this.emit(MKPlayerEvent.Muted, event);
        });
        this.player.on(MKPlayerEvent.Paused, (event: MKPlaybackEvent) => {
            MKAzukiRequest.playerState = MKPlaybackState.Paused;
            this.emit(MKPlayerEvent.Paused, event);
        });
        this.player.on(MKPlayerEvent.PeriodSwitch, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.PeriodSwitch, event);
        });
        this.player.on(MKPlayerEvent.PeriodSwitched, (event: MKPeriodSwitchedEvent) => {
            this.emit(MKPlayerEvent.PeriodSwitched, event);
        });
        this.player.on(MKPlayerEvent.Play, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.Play, event);
        });
        this.player.on(MKPlayerEvent.PlaybackFinished, (event: MKPlayerEventBase) => {
            this.clearLowLatencyInterval();
            this.emit(MKPlayerEvent.PlaybackFinished, event);
        });
        this.player.on(MKPlayerEvent.PlaybackSpeedChanged, (event: MKPlaybackSpeedChangedEvent) => {
            this.emit(MKPlayerEvent.PlaybackSpeedChanged, event);
        });
        this.player.on(MKPlayerEvent.PlayerResized, (event: MKPlayerResizedEvent) => {
            this.emit(MKPlayerEvent.PlayerResized, event);
        });
        this.player.on(MKPlayerEvent.Playing, (event: MKPlaybackEvent) => {
            MKAzukiRequest.playerState = MKPlaybackState.Playing;
            this.emit(MKPlayerEvent.Playing, event);
        });
        this.player.on(MKPlayerEvent.Ready, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.Ready, event);
            this.llCustomDataFieldDef = this.sourceConfig?.live?.lowLatencyAnalyticsCustomDataField ?? 0;
            if (this.clientPrefersLowLatencyPlayback && this.llCustomDataFieldDef >= 1 && this.llCustomDataFieldDef <= 50) {
                this.lowLatencyAnalyticsInterval = setInterval(() => {
                    const lowLatencyAnalytics = `LL=${this.lowLatencyPlayback}, TL=${this.lowLatencyPlayback ? this.lowlatency.getTargetLatency() : 0}, CL=${this.lowlatency.getLatency().toFixed(3)}, LE=${(this.player.isLive() && this.player.getTimeShift() === 0)}`; 
                    MKLog.debug("Logging LowLatency Metrics: ", lowLatencyAnalytics);
                    this.setAnalyticsCustomData({
                        [`customData${this.llCustomDataFieldDef}`]: lowLatencyAnalytics
                    });
                }, 60000);
            } else {
                if (!this.clientPrefersLowLatencyPlayback) {
                    MKLog.debug("Low latency feature is not enabled, LowLatency analytics will not be logged");
                }
                if (!this.llCustomDataFieldDef) {
                    MKLog.debug("Low latency customDataField is not set, LowLatency analytics will not be logged");
                }
            }
        });
        this.player.on(MKPlayerEvent.Seek, (event: MKSeekEvent) => {
            this.emit(MKPlayerEvent.Seek, event);
        });
        this.player.on(MKPlayerEvent.Seeked, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.Seeked, event);
        });
        this.player.on(MKPlayerEvent.SegmentPlayback, (event: MKSegmentPlaybackEvent) => {
            this.emit(MKPlayerEvent.SegmentPlayback, event);
        });
        this.player.on(MKPlayerEvent.SegmentRequestFinished, (event: MKSegmentRequestFinishedEvent) => {
            this.emit(MKPlayerEvent.SegmentRequestFinished, event);
        });
        this.player.on(MKPlayerEvent.ShowAirplayTargetPicker, (event: MKPlayerEventBase) => {
            this.emit(MKPlayerEvent.ShowAirplayTargetPicker, event);
        });
        this.player.on(MKPlayerEvent.SourceLoaded, (event: MKPlayerEventBase) => {
            const azukiSessionId = this.azukiRequest?.getAzukiSessionId();
            this.setAnalyticsCustomData({
                customData10: azukiSessionId ? `${this.getVersion()}_${azukiSessionId}` : `${this.getVersion()}`
            });

            // update source loaded state and emit the event
            this.sourceLoaded = true;

            // event data from player engine could contain customData which has streamType and streamUrl
            // so extract that information if available and update currentSourceInfo.
            if (event.customData) {
                const streamInfo = event.customData as unknown as {streamType: MKStreamType, streamUrl: string};
                if (streamInfo.streamType !== MKStreamType.Unknown && !!streamInfo.streamUrl) {
                    Object.assign(this.currentSourceInfo, {
                        streamUrl: streamInfo.streamUrl,
                        streamType: streamInfo.streamType
                    });
                }
            }

            // add source loaded event custom data to the source loaded event.
            Object.assign(event, {
                customData: this.currentSourceInfo
            });

            // emit the source loaded event.
            this.emit(MKPlayerEvent.SourceLoaded, event);

            // Initialize and setup session concurrency manager
            if (this.sourceConfig?.registered?.adobePrimetimeConfig && this.sourceConfig?.registered?.adobePrimetimeConfig.endpointUrl?.trim()) {
                try {
                    // Initialize adobe session concurrency request and set it as the current session manager
                    this.adobeSessionConcurrencyRequest = new MKAdobeSessionConcurrencyRequest(this.sourceConfig.registered.adobePrimetimeConfig, this.onSessionConcurrencyNotificationError.bind(this));
                    this.sessionManager = this.adobeSessionConcurrencyRequest;
                } catch (error) {
                    MKLog.warn("Failed to initialize session manager due to error: ", JSON.stringify(error));
                }
            } else {
                // set azuki as the current session manager
                this.sessionManager = this.azukiRequest;
            }

            // start session concurrency notifications.
            this.sessionManager?.startSession()
            .catch((error) => {
                MKLog.warn("startSession failed for session concurrency with error: ", JSON.stringify(error));
            });

            // enable thumbnail manager if available.
            if (this.playerConfig.enableHlsImageMediaPlaylistSupport &&
                this.sourceConfig?.hls && !this.sourceConfig?.thumbnailTrack) {
                this.thumbnailManager.enable(this.sourceConfig.hls, this.headers ?? undefined);
            }
        });
        this.player.on(MKPlayerEvent.SourceUnloaded, (event: MKPlayerEventBase) => {
            this.sourceLoaded = false;
            this.clearLowLatencyInterval();
            this.emit(MKPlayerEvent.SourceUnloaded, event);
        });
        this.player.on(MKPlayerEvent.StallEnded, (event: MKPlayerEventBase) => {
            MKAzukiRequest.playerState = MKPlaybackState.Playing;
            this.emit(MKPlayerEvent.StallEnded, event);
        });
        this.player.on(MKPlayerEvent.StallStarted, (event: MKPlayerEventBase) => {
            MKAzukiRequest.playerState = MKPlaybackState.Buffering;
            this.emit(MKPlayerEvent.StallStarted, event);
        });
        this.player.on(MKPlayerEvent.SubtitleAdded, (event: MKSubtitleEvent) => {
            this.emit(MKPlayerEvent.SubtitleAdded, event);
        });
        this.player.on(MKPlayerEvent.SubtitleDisable, (event: MKSubtitleEvent) => {
            this.emit(MKPlayerEvent.SubtitleDisable, event);
        });
        this.player.on(MKPlayerEvent.SubtitleDisabled, (event: MKSubtitleEvent) => {
            this.emit(MKPlayerEvent.SubtitleDisabled, event);
        });
        this.player.on(MKPlayerEvent.SubtitleEnable, (event: MKSubtitleEvent) => {
            this.emit(MKPlayerEvent.SubtitleEnable, event);
        });
        this.player.on(MKPlayerEvent.SubtitleEnabled, (event: MKSubtitleEvent) => {
            this.emit(MKPlayerEvent.SubtitleEnabled, event);
        });
        this.player.on(MKPlayerEvent.SubtitleRemoved, (event: MKSubtitleEvent) => {
            this.emit(MKPlayerEvent.SubtitleRemoved, event);
        });
        this.player.on(MKPlayerEvent.TargetLatencyChanged, (event: MKTargetLatencyChangedEvent) => {
            this.emit(MKPlayerEvent.TargetLatencyChanged, event);
        });
        this.player.on(MKPlayerEvent.TimeChanged, (event: MKPlaybackEvent) => {
            // emit time changed event
            this.emit(MKPlayerEvent.TimeChanged, event);

            // time changed event data
            const timeChangedEvent = event as unknown as MKPlaybackEvent;

            if ((this.sourceConfig?.assetType === MKAssetType.VOD) && MKAzukiRequest.enableUserEngagementData) {
                MKAzukiRequest.relativePlaybackLocation = Math.round(this.player.getCurrentTime());
            }

            // check and handle program boundary cross
            if (this.programManager && this.sourceConfig?.assetType === MKAssetType.Live) {
                const programInfo = this.programManager.checkProgramBoundary(timeChangedEvent.time);
                if (programInfo) {
                    try {
                        // validate program restrictions
                        const controlBlockingRestrictions = this.programManager.validateProgramRestrictions(programInfo);
                        if (controlBlockingRestrictions.length > 0) {
                            this.emitProgramRestrictionsEvent(controlBlockingRestrictions);
                        }

                        // emit program time changed event on boundary cross
                        if (programInfo.timeRange) {
                            this.emitProgramTimeChangedEvent(programInfo.timeRange);
                        }
                    } catch (error) {
                        // Playback is not allowed on boundary cross
                        // stop playback and emit the error event
                        if (MKUtils.isMKError(error)) {
                            this.unloadAndEmitErrorEvent(error as unknown as MKError);
                        } else {
                            MKLog.error("Unhandled error: ", error);
                        }
                    }
                }
            }
        });
        this.player.on(MKPlayerEvent.Unmuted, (event: MKUserInteractionEvent) => {
            this.emit(MKPlayerEvent.Unmuted, event);
        });
        this.player.on(MKPlayerEvent.VideoAdaptation, (event: MKVideoAdaptationEvent) => {
            this.emit(MKPlayerEvent.VideoAdaptation, event);
        });
        this.player.on(MKPlayerEvent.VideoDownloadQualityChange, (event: MKVideoDownloadQualityChangeEvent) => {
            this.emit(MKPlayerEvent.VideoDownloadQualityChange, event);
        });
        this.player.on(MKPlayerEvent.VideoDownloadQualityChanged, (event: MKVideoDownloadQualityChangeEvent) => {
            this.emit(MKPlayerEvent.VideoDownloadQualityChanged, event);
        });
        this.player.on(MKPlayerEvent.VideoPlaybackQualityChanged, (event: MKVideoPlaybackQualityChangedEvent) => {
            this.emit(MKPlayerEvent.VideoPlaybackQualityChanged, event);
        });
        this.player.on(MKPlayerEvent.VideoQualityAdded, (event: MKVideoQualityEvent) => {
            this.emit(MKPlayerEvent.VideoQualityAdded, event);
        });
        this.player.on(MKPlayerEvent.VideoQualityChanged, (event: MKVideoQualityChangedEvent) => {
            this.emit(MKPlayerEvent.VideoQualityChanged, event);
        });
        this.player.on(MKPlayerEvent.VideoQualityRemoved, (event: MKVideoQualityEvent) => {
            this.emit(MKPlayerEvent.VideoQualityRemoved, event);
        });
        this.player.on(MKPlayerEvent.ViewModeChanged, (event: MKViewModeChangedEvent) => {
            this.emit(MKPlayerEvent.ViewModeChanged, event);
        });
        this.player.on(MKPlayerEvent.VolumeChanged, (event: MKVolumeChangedEvent) => {
            this.emit(MKPlayerEvent.VolumeChanged, event);
        });
        this.player.on(MKPlayerEvent.VSPPSessionIdAvailable, (event: MKVSPPSessionIdEvent) => {
            this.emit(MKPlayerEvent.VSPPSessionIdAvailable, event);
        });
    }

    /**
     * Emit program time changed event with the given time range.
     * @param timeRange the time range to be used as event data.
     */
    private emitProgramTimeChangedEvent(timeRange: MKTimeRange) {
        // program time changed event data
        const programTimeChangedEventData: MKProgramTimeChangedEvent = {
            timeRange: timeRange,
            type: MKPlayerEvent.ProgramTimeChanged,
            timestamp: MKUtils.getCurrentTimestamp()
        };

        // emit program time changed event
        this.emit(MKPlayerEvent.ProgramTimeChanged, programTimeChangedEventData);
    }

    /**
     * Stop playbck and emit an error event.
     * @param error the error event data to emit.
     * @param unloadPlayer `true` by default will unload the player, when `false` will only emit error event without unloading player.
     */
    private unloadAndEmitErrorEvent(error: MKError, unloadPlayer: boolean = true): MKError {
        // unload to stop playback.
        if (unloadPlayer) this.unload();

        // emit error event
        const errorEvent: MKErrorEvent = {
            name: error.name,
            code: error.code,
            message: error.message,
            type: MKPlayerEvent.Error,
            timestamp: MKUtils.getCurrentTimestamp()
        }

        // emit error event
        this.emit(MKPlayerEvent.Error, errorEvent);
        return errorEvent;
    }

    /**
     * Emit program restrictions event for all control blocking restrictions.
     * @param restrictions control blocking program restrictions to emit with the event.
     */
    private emitProgramRestrictionsEvent(restrictions: MKError[]) {
        if (restrictions.length > 0) {
            // program restrictions event data
            const event: MKProgramRestrictionsEvent = {
                type: MKPlayerEvent.ProgramRestrictions,
                timestamp: MKUtils.getCurrentTimestamp(),
                programRestrictions: restrictions,
            }

            // emit program restrictions event
            this.emit(MKPlayerEvent.ProgramRestrictions, event);
        }
    }

    /**
     * Error callback to handle session concurrency notification errors.
     *
     * This callback handles error from either Adobe Session Concurrency or Azuki Beacons.
     *
     * @param error object containing error code and message.
     */
    private onSessionConcurrencyNotificationError(error: MKError): void {
        // list of error codes for which the current playback session must be terminated
        const errorCodes = [
            // azuki codes
            "4-63-2413",         // session platform group limit exceeded
            "4-63-2430",         // session concurrency limit exceeded
            "4-63-3098",         // session offer exceeded
            "4-63-3736",         // time limit offer expiry
            // adobe codes
            "4-81-409",          // session conflict
        ];

        if (errorCodes.includes(error.code)) {
            // stop the current session
            this.sessionManager?.stopSession()
            .catch((error) => {
                MKLog.warn("stopSession failed for sesison concurrency with error: ", JSON.stringify(error));
            });

            // terminate the current playback session and emit the error.
            this.unloadAndEmitErrorEvent(error, true);
        } else {
            // non fatal errors - just log them.
            MKLog.warn("Session concurrency notification failed with error: ", JSON.stringify(error));
        }
    }

    /**
     * Adds default values to missing properties in a low latency config.
     *
     * @param lowLatencyConfig - low latency configuration.
     * @returns Complete configuration with defaults applied if any property missing.
     */
    private setLowLatencyConfig(lowLatencyConfig: MKLowLatencyConfig): MKLowLatencyConfig {
        return {
            targetLatency: lowLatencyConfig.targetLatency ?? 8,
            catchup: {
                playbackRateThreshold: lowLatencyConfig.catchup?.playbackRateThreshold ?? 0.075,
                seekThreshold: lowLatencyConfig.catchup?.seekThreshold ?? 5,
                playbackRate: lowLatencyConfig.catchup?.playbackRate ?? 1.08
            },
            fallback: {
                playbackRateThreshold: lowLatencyConfig.fallback?.playbackRateThreshold ?? 0.075,
                seekThreshold: lowLatencyConfig.fallback?.seekThreshold ?? 5,
                playbackRate: lowLatencyConfig.fallback?.playbackRate ?? 0.95
            }
        };
    }

    /**
     * Clears the low latency interval if it is currently active.
     *
     * @returns void.
     */
    private clearLowLatencyInterval(): void {
        if (this.lowLatencyAnalyticsInterval) {
            clearInterval(this.lowLatencyAnalyticsInterval);
            this.lowLatencyAnalyticsInterval = null;
        }
    }
}